/**
 * What the clinic cells need from the server before they can edit.
 *
 * - Every clinic the operator administers, not only the page's first hundred:
 *   a save replaces the member's setup there and leaves every other clinic as
 *   it is, so the grid must know the whole set the server uses.
 * - The agency's role catalogue: the three clinic roles' ids, the names of
 *   other roles a flag may be held through, and the agency roles the Agency
 *   roles editor offers. Loaded whenever the grid is, not only for clinics.
 *
 * The clinics load again with every members reload (`reloadKey`): another
 * administrator may have granted the operator a clinic since, and a stale
 * bound would leave it out of the next save, which the server reads as a
 * revoke. The previous bound stays until the new one arrives. The catalogue
 * loads once. A failure says so once and tries again, quietly, on the next
 * reload. Null until loaded.
 */

import { useEffect, useRef, useState } from 'react';
import { listRoles } from '../../../services/api';
import type { RoleResponse } from '../../../services/api/types.gen';
import { getAgencyClinics } from '../../../services/clinicsApi';
import { withAgencyClinicDisplayNames } from '../../../utils/agencyClinicDisplayName';
import type { AgencyClinicResponse } from '../types';
import { CLINIC_ROLE_SLUGS, type ClinicRoleIds } from './clinicSetup';

/** As many clinics as the server's own bound check reads. */
const ALL_CLINICS = 10000;

export function useClinicSetupData(
  enabled: boolean,
  reloadKey: unknown,
  showError: (message: string) => void
) {
  const [boundClinics, setBoundClinics] = useState<
    AgencyClinicResponse[] | null
  >(null);
  const [catalogue, setCatalogue] = useState<RoleResponse[] | null>(null);
  const [roleIds, setRoleIds] = useState<ClinicRoleIds | null>(null);
  const loadingRef = useRef({ roles: false });
  // A failure that keeps happening is told once, not after every save.
  const toldRef = useRef({ clinics: false, roles: false });

  useEffect(() => {
    if (!enabled) return;
    // Only the latest reload's answer counts.
    let latest = true;
    getAgencyClinics(undefined, 0, ALL_CLINICS)
      .then(data => {
        if (latest) setBoundClinics(withAgencyClinicDisplayNames(data));
      })
      .catch((error: unknown) => {
        console.error('Failed to load the clinics:', error);
        if (latest && !toldRef.current.clinics) {
          toldRef.current.clinics = true;
          showError('Unable to load clinics. Please try again.');
        }
      });
    return () => {
      latest = false;
    };
  }, [enabled, reloadKey, showError]);

  useEffect(() => {
    const loading = loadingRef.current;
    if (!catalogue && !loading.roles) {
      loading.roles = true;
      listRoles({ query: { limit: 500 }, throwOnError: true })
        .then(({ data }) => {
          setCatalogue(data);
          const idOf = (slug: string) => {
            const id = data.find(
              role => role.scope === 'clinic' && role.slug === slug
            )?.id;
            if (!id) {
              console.error(
                `The clinic role "${slug}" is not in the role catalogue, so clinic roles stay read-only in the members grid.`
              );
            }
            return id;
          };
          const clinician = idOf(CLINIC_ROLE_SLUGS.clinician);
          const caseManager = idOf(CLINIC_ROLE_SLUGS.caseManager);
          const clinicalManager = idOf(CLINIC_ROLE_SLUGS.clinicalManager);
          if (clinician && caseManager && clinicalManager) {
            setRoleIds({ clinician, caseManager, clinicalManager });
          }
        })
        .catch((error: unknown) => {
          console.error('Failed to load the roles:', error);
          if (!toldRef.current.roles) {
            toldRef.current.roles = true;
            showError('Unable to load roles. Please try again.');
          }
        })
        .finally(() => {
          loading.roles = false;
        });
    }
  }, [catalogue, reloadKey, showError]);

  return { boundClinics, catalogue, roleIds };
}
